import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createInvoice } from '../../services/api';
import { formatCurrency } from '../../utils/formatCurrency';
import './QuickInvoiceModal.css';

const RECEPTOR_CONDICION_IVA = [
  { value: 5, label: 'Consumidor Final' },
  { value: 1, label: 'IVA Responsable Inscripto' },
  { value: 6, label: 'Responsable Monotributo' },
  { value: 4, label: 'IVA Sujeto Exento' },
];

function QuickInvoiceModal({ orders, restaurantId, onClose, onCreated }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('cf'); // 'cf' = consumidor final, 'other' = datos receptor
  const [receptor, setReceptor] = useState({
    docTipo: 80,
    docNro: '',
    condicionIVA: 1,
    nombre: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const paidOrders = orders.filter(
    (o) => o.paymentStatus === 'paid' && !o.invoiceId && o.status !== 'cancelled'
  );
  const orderIds = paidOrders.map((o) => o.orderId);
  const total = paidOrders.reduce((sum, o) => sum + (o.total || 0), 0);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const data = {
        restaurantId,
        orderIds,
        receptor:
          mode === 'cf'
            ? { docTipo: 99, docNro: 0, condicionIVA: 5, nombre: 'Consumidor Final' }
            : receptor,
      };

      const res = await createInvoice(data);
      setResult(res.data);
    } catch (err) {
      setError(
        err.response?.data?.afipError ||
          err.response?.data?.error ||
          t('invoicing.createError', 'Error al facturar')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (paidOrders.length === 0) {
    return (
      <div className="quick-invoice-overlay" onClick={onClose}>
        <div className="quick-invoice-modal" onClick={(e) => e.stopPropagation()}>
          <div className="quick-invoice-header">
            <h3>{t('invoicing.title', 'Facturar')}</h3>
            <button className="quick-invoice-close" onClick={onClose}>✕</button>
          </div>
          <div className="quick-invoice-body">
            <p className="quick-invoice-empty">
              {t('invoicing.noPaidOrders', 'No hay pedidos pagados sin facturar')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quick-invoice-overlay" onClick={onClose}>
      <div className="quick-invoice-modal" onClick={(e) => e.stopPropagation()}>
        <div className="quick-invoice-header">
          <h3>{t('invoicing.title', 'Facturar')}</h3>
          <button className="quick-invoice-close" onClick={onClose}>✕</button>
        </div>

        <div className="quick-invoice-body">
          {error && <div className="quick-invoice-error">{error}</div>}

          {result ? (
            <div className="quick-invoice-result">
              <span className="quick-invoice-check">✓</span>
              <h4>{t('invoicing.success', 'Factura emitida')}</h4>
              <div className="quick-invoice-result-info">
                <span>Factura {result.invoiceClass} {String(result.puntoDeVenta).padStart(5, '0')}-{String(result.numeroComprobante).padStart(8, '0')}</span>
                <span>CAE: {result.cae}</span>
                <span>{formatCurrency(result.importeTotal)}</span>
              </div>
              <button className="detail-action-btn" onClick={onCreated}>
                {t('common.close', 'Cerrar')}
              </button>
            </div>
          ) : (
            <>
              <div className="quick-invoice-summary">
                <span>{t('invoicing.orders', { count: paidOrders.length })}</span>
                <span className="quick-invoice-total">{formatCurrency(total)}</span>
              </div>

              <div className="quick-invoice-mode">
                <button
                  className={`quick-invoice-mode-btn ${mode === 'cf' ? 'active' : ''}`}
                  onClick={() => setMode('cf')}
                >
                  {t('invoicing.consumidorFinal', 'Consumidor Final')}
                </button>
                <button
                  className={`quick-invoice-mode-btn ${mode === 'other' ? 'active' : ''}`}
                  onClick={() => setMode('other')}
                >
                  {t('invoicing.otherReceptor', 'Otros datos')}
                </button>
              </div>

              {mode === 'other' && (
                <div className="quick-invoice-form">
                  <select
                    value={receptor.condicionIVA}
                    onChange={(e) =>
                      setReceptor({ ...receptor, condicionIVA: parseInt(e.target.value, 10) })
                    }
                  >
                    {RECEPTOR_CONDICION_IVA.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={receptor.docNro}
                    onChange={(e) =>
                      setReceptor({ ...receptor, docNro: e.target.value.replace(/\D/g, '') })
                    }
                    placeholder="CUIT (11 dígitos)"
                  />
                  <input
                    type="text"
                    value={receptor.nombre}
                    onChange={(e) => setReceptor({ ...receptor, nombre: e.target.value })}
                    placeholder={t('invoicing.receptorName', 'Nombre / Razón Social')}
                  />
                </div>
              )}

              <button
                className="detail-action-btn pay-cash-btn"
                onClick={handleSubmit}
                disabled={submitting}
                style={{ width: '100%', marginTop: '12px' }}
              >
                {submitting
                  ? t('invoicing.creating', 'Emitiendo...')
                  : t('invoicing.emit', 'Emitir Factura')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuickInvoiceModal;
